import React, { useCallback, useEffect, useState } from "react";
import ModalComponent from "../../../components/modals";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import SimpleSelect from "../../../components/selects/simpleSelect";
import ShowTable from "../../../components/tables/showTable";
import {
  getBiomedicalCatalogItemSummary,
  postDeleteOrMigrateBiomedicalCatalogItem
} from "../../../services/apiService";
import ToastNotification from "../../../components/toastNotification";

const EQUIPMENT_TABLE_COLUMNS = [
  { field: "id", header: "ID", width: "72px" },
  { field: "name", header: "Nombre" },
  { field: "brand_name", header: "Marca" },
  { field: "model", header: "Modelo" },
  { field: "serie_name", header: "Serie" }
];

const TEMPLATE_TABLE_COLUMNS = [
  { field: "model_id", header: "ID Modelo", width: "96px" },
  { field: "model_name", header: "Modelo" },
  { field: "brand_name", header: "Marca" }
];

const DeleteCatalogItemModal = ({
  open,
  close,
  itemRow,
  allRows,
  userId,
  catalogDef,
  onDone
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "warning",
    summary: "",
    detail: ""
  });
  const [relatedEquipment, setRelatedEquipment] = useState([]);
  const [relatedTemplates, setRelatedTemplates] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { control, reset, handleSubmit, watch } = useForm({
    defaultValues: { migrateToId: "" }
  });

  const migrateToWatch = watch("migrateToId");

  const migrateOptions = (allRows || [])
    .filter((row) => Number(row.value) !== Number(itemRow?.value))
    .map((row) => ({ name: row.name, value: String(row.value) }));

  const hasMigrateTargets = migrateOptions.length > 0;
  const relatedCount = relatedEquipment.length;
  const hasRelatedEquipment = relatedCount > 0;
  const templateCount = relatedTemplates.length;
  const hasRelatedTemplates = templateCount > 0;
  const supportsMigration = catalogDef.supportsMigration !== false;

  const pushToast = useCallback((severity, summary, detail) => {
    setToastConfig({ severity, summary, detail });
    setShowToast(true);
  }, []);

  const loadEquipmentSummary = useCallback(async () => {
    const itemId = itemRow?.value;
    if (itemId == null || itemId === "" || !userId) {
      setRelatedEquipment([]);
      setRelatedTemplates([]);
      setSummaryError(null);
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const { data } = await getBiomedicalCatalogItemSummary(
        catalogDef.catalogType,
        itemId,
        userId
      );
      setRelatedEquipment(Array.isArray(data?.equipment) ? data.equipment : []);
      setRelatedTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        `No se pudo cargar el listado de equipos para este ${catalogDef.labelSingular}.`;
      setSummaryError(msg);
      setRelatedEquipment([]);
      setRelatedTemplates([]);
      pushToast("error", "Error", msg);
    } finally {
      setSummaryLoading(false);
    }
  }, [catalogDef.catalogType, catalogDef.labelSingular, itemRow?.value, pushToast, userId]);

  useEffect(() => {
    if (open && itemRow?.value != null && userId) {
      reset({ migrateToId: "" });
      loadEquipmentSummary();
    } else if (!open) {
      setRelatedEquipment([]);
      setRelatedTemplates([]);
      setSummaryError(null);
      reset({ migrateToId: "" });
    }
  }, [open, itemRow?.value, userId, reset, loadEquipmentSummary]);

  const handleClose = () => {
    setRelatedEquipment([]);
    setRelatedTemplates([]);
    setSummaryError(null);
    reset();
    close();
  };

  const submitDelete = async (formValues) => {
    if (!itemRow?.value || !userId) return;

    if (summaryLoading || summaryError) {
      pushToast(
        "warn",
        "Espere",
        `Espere a que termine de cargar la información de ${catalogDef.labelArticle}.`
      );
      return;
    }

    if (!supportsMigration && (hasRelatedEquipment || hasRelatedTemplates)) {
      pushToast(
        "warn",
        "Eliminación bloqueada",
        `No puede eliminar ${catalogDef.labelArticle} mientras tenga referencias activas.`
      );
      return;
    }

    if (supportsMigration && hasRelatedEquipment) {
      if (!hasMigrateTargets) {
        pushToast(
          "error",
          `Sin ${catalogDef.labelSingular} destino`,
          `${catalogDef.capitalizeLabel} tiene ${relatedCount} equipo(s) biomédico(s) asignado(s), pero no hay otro valor en el catálogo. Cree uno nuevo y vuelva a intentar.`
        );
        return;
      }

      const destStr =
        formValues.migrateToId != null ? String(formValues.migrateToId).trim() : "";
      if (!destStr) {
        pushToast(
          "warn",
          `${catalogDef.capitalizeLabel} destino requerido`,
          `Debe elegir el nuevo ${catalogDef.labelSingular} para los ${relatedCount} equipo(s) antes de eliminar.`
        );
        return;
      }
    }

    setLoading(true);
    try {
      const migrateTo =
        hasRelatedEquipment &&
        formValues.migrateToId &&
        String(formValues.migrateToId).trim() !== ""
          ? Number(formValues.migrateToId)
          : undefined;

      await postDeleteOrMigrateBiomedicalCatalogItem({
        userId,
        catalogType: catalogDef.catalogType,
        id: itemRow.value,
        migrateToId: migrateTo
      });
      onDone?.();
      handleClose();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.equipmentCount != null) {
        await loadEquipmentSummary();
        pushToast(
          "warning",
          "Migración requerida",
          `Hay ${data.equipmentCount} equipo(s) usando este ${catalogDef.labelSingular}. Elija un destino y confirme de nuevo.`
        );
      } else {
        const msg =
          typeof data?.error === "string"
            ? data.error
            : `No se pudo eliminar ${catalogDef.labelArticle}.`;
        pushToast("error", "Error", msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const tableData = relatedEquipment.map((row) => ({
    ...row,
    brand_name: row.brand_name ?? "—",
    serie_name: row.serie_name ?? "—",
    model: row.model ?? "—"
  }));

  const templateData = relatedTemplates.map((row) => ({
    ...row,
    brand_name: row.brand_name ?? "—",
    model_name: row.model_name ?? "—"
  }));

  const submitDisabled =
    loading ||
    summaryLoading ||
    Boolean(summaryError) ||
    (!supportsMigration && (hasRelatedEquipment || hasRelatedTemplates)) ||
    (supportsMigration &&
      hasRelatedEquipment &&
      (!hasMigrateTargets ||
        !migrateToWatch ||
        String(migrateToWatch).trim() === ""));

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title={`Eliminar ${catalogDef.labelSingular}`}
        maxWidth="800px"
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <div className="HContainer">
          <form
            className="VContainer align-center m-20"
            onSubmit={handleSubmit(submitDelete)}
            style={{ width: "100%", maxWidth: "760px" }}
          >
            <p className="mb-15" style={{ maxWidth: "640px" }}>
              ¿Eliminar {catalogDef.labelArticle} <strong>{itemRow?.name}</strong>?
            </p>

            {summaryLoading && (
              <p className="mb-15">
                Cargando referencias vinculadas a {catalogDef.labelArticle}…
              </p>
            )}

            {summaryError && !summaryLoading && (
              <p className="mb-15" style={{ color: "#c62828" }}>
                {summaryError}
              </p>
            )}

            {!summaryLoading &&
              !summaryError &&
              !hasRelatedEquipment &&
              !hasRelatedTemplates && (
              <p className="mb-15" style={{ maxWidth: "640px", color: "#2e7d32" }}>
                No hay referencias activas con este valor. Puede eliminarlo de
                forma directa.
              </p>
            )}

            {!summaryLoading && !summaryError && supportsMigration && hasRelatedEquipment && (
              <>
                <div
                  className="mb-15"
                  style={{
                    maxWidth: "640px",
                    padding: "12px 14px",
                    background: "#fff3e0",
                    borderRadius: "8px",
                    border: "1px solid #ffb74d"
                  }}
                  role="alert"
                >
                  <strong>No se puede eliminar {catalogDef.labelArticle} todavía.</strong>{" "}
                  Tiene <strong>{relatedCount}</strong> equipo(s) biomédico(s) con
                  este valor asignado. Para eliminarlo debe escoger un{" "}
                  <strong>nuevo {catalogDef.labelSingular}</strong> para esos equipos;
                  se migrarán y luego se eliminará el valor actual.
                </div>

                <div className="mb-15 w-complete" style={{ maxHeight: "320px" }}>
                  <p className="create-label mb-8">Resumen de equipos a migrar</p>
                  <ShowTable
                    data={tableData}
                    columns={EQUIPMENT_TABLE_COLUMNS}
                    pagination
                    rows={10}
                    scrollable
                    scrollHeight="240px"
                    stripedRows
                    compact
                    emptyMessage="Sin equipos vinculados"
                  />
                </div>
              </>
            )}

            {!summaryLoading && !summaryError && !supportsMigration && (
              <>
                {(hasRelatedEquipment || hasRelatedTemplates) && (
                  <div
                    className="mb-15"
                    style={{
                      maxWidth: "640px",
                      padding: "12px 14px",
                      background: "#fff3e0",
                      borderRadius: "8px",
                      border: "1px solid #ffb74d"
                    }}
                    role="alert"
                  >
                    <strong>No se puede eliminar {catalogDef.labelArticle}.</strong>{" "}
                    {catalogDef.deleteBlockedUsageText ||
                      "Debe dejar de usarse en eventos de equipos y plantillas por modelo antes de eliminarlo."}
                  </div>
                )}

                {hasRelatedEquipment && (
                  <div className="mb-15 w-complete" style={{ maxHeight: "320px" }}>
                    <p className="create-label mb-8">Equipos que usan este valor</p>
                    <ShowTable
                      data={tableData}
                      columns={EQUIPMENT_TABLE_COLUMNS}
                      pagination
                      rows={10}
                      scrollable
                      scrollHeight="240px"
                      stripedRows
                      compact
                      emptyMessage="Sin equipos vinculados"
                    />
                  </div>
                )}

                {hasRelatedTemplates && (
                  <div className="mb-15 w-complete" style={{ maxHeight: "320px" }}>
                    <p className="create-label mb-8">
                      {catalogDef.relatedTableTitle ||
                        "Plantillas por modelo que usan este valor"}
                    </p>
                    <ShowTable
                      data={templateData}
                      columns={TEMPLATE_TABLE_COLUMNS}
                      pagination
                      rows={10}
                      scrollable
                      scrollHeight="240px"
                      stripedRows
                      compact
                      emptyMessage={
                        catalogDef.relatedEmptyMessage || "Sin plantillas vinculadas"
                      }
                    />
                  </div>
                )}
              </>
            )}

            {supportsMigration &&
              hasRelatedEquipment &&
              !hasMigrateTargets &&
              !summaryLoading && (
              <p className="mb-15" style={{ maxWidth: "640px", color: "#c62828" }}>
                No hay otro valor disponible en el catálogo. Cree un nuevo{" "}
                {catalogDef.labelSingular} y vuelva a abrir este diálogo.
              </p>
              )}

            {supportsMigration &&
              hasRelatedEquipment &&
              hasMigrateTargets &&
              !summaryLoading && (
              <Grid container spacing={2} className="w-complete">
                <Grid item xs={12}>
                  <p className="create-label">
                    Nuevo {catalogDef.labelSingular} para los {relatedCount} equipo(s)
                  </p>
                  <SimpleSelect
                    name="migrateToId"
                    control={control}
                    label=""
                    options={migrateOptions}
                    margin="0"
                  />
                </Grid>
              </Grid>
              )}

            <div className="HContainer j-center mt-20">
              <LaddaButton
                type="submit"
                variant="contained"
                disabled={submitDisabled}
              >
                {supportsMigration && hasRelatedEquipment
                  ? `Migrar equipos y eliminar ${catalogDef.labelSingular}`
                  : "Eliminar"}
              </LaddaButton>
            </div>
          </form>
        </div>
      </ModalComponent>
      <ToastNotification
        showToast={showToast}
        setShowToast={setShowToast}
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
      />
    </>
  );
};

export default DeleteCatalogItemModal;
